import { MoreOutlined, StarFilled, StarOutlined } from "@ant-design/icons"
import { Button, Dropdown, Flex, Table, type TableProps, Typography } from "antd"
import type { MenuItemType } from "antd/es/menu/interface"
import React from "react"
import { useTranslation } from "react-i18next"

import { getFormatDate } from "../../../utils/date/get-date-safe.ts"
import { getFileIcon } from "../../Project/ProjectViewPage/helpers/file.tsx"
import type { DocumentTypeOption } from "../hooks/useDocumentTypeOptions.ts"
import type { StatusOption } from "../hooks/useStatusOptions.ts"
import { MetadataSelect } from "./MetadataSelect.tsx"
import { type FileItem } from "../types/file-item.types.ts"

const { Text } = Typography

interface FilesListViewProps {
  items: FileItem[]
  actions: (file: FileItem) => MenuItemType[]
  selectedKeys: Set<string>
  onToggleSelect: (file: FileItem) => void
  onPreview: (file: FileItem) => void
  isFavorite?: (file: FileItem) => boolean
  onToggleFavorite?: (file: FileItem) => void
  /** Present only where the caller can edit document classification — a `document`-sourced row with edit rights. */
  documentTypeOptions?: DocumentTypeOption[]
  documentTypesFailed?: boolean
  onChangeDocumentType?: (file: FileItem, value: string | null) => Promise<void>
  statusOptions?: StatusOption[]
  statusesFailed?: boolean
  onChangeStatus?: (file: FileItem, value: string | null) => Promise<void>
}

const EMPTY_CELL = "—"

export function FilesListView({
  items,
  actions,
  selectedKeys,
  onToggleSelect,
  onPreview,
  isFavorite,
  onToggleFavorite,
  documentTypeOptions = [],
  documentTypesFailed = false,
  onChangeDocumentType,
  statusOptions = [],
  statusesFailed = false,
  onChangeStatus
}: FilesListViewProps) {
  const { t } = useTranslation()

  const columns: TableProps<FileItem>["columns"] = [
    {
      title: t("files.columns.name"),
      key: "name",
      align: "left",
      render: (_, file) => (
        <Flex align="center" gap="small">
          {onToggleFavorite && (
            <Button
              type="text"
              size="small"
              aria-label={t("files.actions.favorite", { name: file.name })}
              icon={
                isFavorite?.(file) ? (
                  <StarFilled className="text-warning" />
                ) : (
                  <StarOutlined />
                )
              }
              onClick={() => onToggleFavorite(file)}
            />
          )}
          <span className="[&_svg]:text-base">{getFileIcon(file.mimeType)}</span>
          <a onClick={() => onPreview(file)}>
            <Text ellipsis={{ tooltip: file.name }}>{file.name}</Text>
          </a>
        </Flex>
      )
    },
    {
      title: t("files.columns.type"),
      key: "type",
      render: (_, file) =>
        file.fileTypeLabel ??
        (file.source === "album-attachment"
          ? t("files.columns.typeFile")
          : t("files.columns.typeDocument"))
    },
    {
      title: t("files.columns.size"),
      key: "size",
      render: (_, file) => file.formattedFileSize || EMPTY_CELL
    },
    {
      title: t("files.columns.documentType"),
      key: "documentType",
      responsive: ["lg"],
      render: (_, file) => {
        // Attachment (album photos) carries no classification at all — an
        // Attachment/photo row, not just an unset value on a document.
        if (file.source !== "document") return EMPTY_CELL
        if (!file.canMove || !onChangeDocumentType) return file.documentTypeLabel || EMPTY_CELL
        return (
          <MetadataSelect
            size="small"
            ariaLabel={t("files.columns.documentType")}
            value={file.documentType}
            options={documentTypeOptions}
            loadFailedText={documentTypesFailed ? t("files.documentType.loadFailed") : undefined}
            placeholder={t("files.documentType.assignType")}
            className="min-w-32"
            onChange={(value) => void onChangeDocumentType(file, value)}
          />
        )
      }
    },
    {
      title: t("files.columns.status"),
      key: "status",
      responsive: ["lg"],
      render: (_, file) => {
        // Status is a `core.ProjectAttachment`-only concept, same as Document
        // Type — a photo has no workflow to opt into.
        if (file.source !== "document") return EMPTY_CELL
        if (!file.canMove || !onChangeStatus) return file.statusLabel || EMPTY_CELL
        return (
          <MetadataSelect
            size="small"
            ariaLabel={t("files.columns.status")}
            value={file.status}
            options={statusOptions}
            loadFailedText={statusesFailed ? t("files.status.loadFailed") : undefined}
            placeholder={t("files.status.setStatus")}
            className="min-w-32"
            onChange={(value) => void onChangeStatus(file, value)}
          />
        )
      }
    },
    {
      title: t("files.columns.version"),
      key: "version",
      responsive: ["lg"],
      render: (_, file) =>
        file.version !== undefined
          ? t("fileViewer.detailsPanel.versionShort", { version: file.version })
          : EMPTY_CELL
    },
    {
      title: t("files.columns.modified"),
      key: "modified",
      render: (_, file) => getFormatDate(file.updated) ?? EMPTY_CELL
    },
    {
      title: t("files.columns.actions"),
      key: "actions",
      width: 80,
      render: (_, file) => (
        <Dropdown trigger={["click"]} menu={{ items: actions(file) }}>
          <Button
            type="text"
            size="small"
            icon={<MoreOutlined />}
            aria-label={t("files.actions.menu", { name: file.name })}
          />
        </Dropdown>
      )
    }
  ]

  return (
    <Table<FileItem>
      rowKey="key"
      size="small"
      columns={columns}
      dataSource={items}
      pagination={false}
      rowSelection={{
        selectedRowKeys: [...selectedKeys],
        onSelect: (file) => onToggleSelect(file)
      }}
    />
  )
}
